import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Progress, Tag, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { useDispatch } from 'src/store/use-dispatch';
import { getLang } from 'shared/store/selectors/lang.selector';
import { fetchUsers } from 'shared/store/actions/users-management.actions';
import { formatNumber } from 'shared/utils';
import { Button, Modal } from 'src/components/design-system';
import {
  Table,
  RadioSetFilter,
  radioSetFilterDoesFilterPass,
} from 'src/components/design-system/table';
import { FileUpload } from 'src/components/core';
import { BULK_AVATAR_STATUS, type BulkAvatarStatus } from './bulk-avatar-upload.utils';
import {
  BULK_AVATAR_PROCESS_ERRORS,
  BULK_AVATAR_STAGES,
  type BulkAvatarResult,
  useBulkAvatarUpload,
} from './use-bulk-avatar-upload.hook';
import styles from './bulk-avatar-upload.module.scss';

const { Text } = Typography;

const ZIP_ACCEPT = {
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
};

const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const STATUS_FILTER = {
  UPLOADED: 'uploaded',
  FAILED: 'failed',
};

export function BulkAvatarUpload() {
  const lang = useSelector(getLang('USERS_MANAGEMENT'));
  const generalLang = useSelector(getLang('GENERAL'));
  const dispatch = useDispatch();
  const { stage, progress, results, processError, processZip, reset } = useBulkAvatarUpload();
  const [isOpen, setIsOpen] = useState(false);
  const [zipFile, setZipFile] = useState<File | null>(null);

  const reasonByStatus: Partial<Record<BulkAvatarStatus, string>> = useMemo(
    () => ({
      [BULK_AVATAR_STATUS.EMAIL_NOT_FOUND]: lang.BULK_AVATAR_REASON_EMAIL_NOT_FOUND,
      [BULK_AVATAR_STATUS.UNSUPPORTED_TYPE]: lang.BULK_AVATAR_REASON_UNSUPPORTED_TYPE,
      [BULK_AVATAR_STATUS.TOO_LARGE]: lang.BULK_AVATAR_REASON_TOO_LARGE,
      [BULK_AVATAR_STATUS.DUPLICATE]: lang.BULK_AVATAR_REASON_DUPLICATE,
      [BULK_AVATAR_STATUS.NETWORK_ERROR]: lang.BULK_AVATAR_REASON_NETWORK_ERROR,
      [BULK_AVATAR_STATUS.UPLOAD_FAILED]: lang.BULK_AVATAR_REASON_UPLOAD_FAILED,
    }),
    [lang],
  );

  const open = useCallback(() => setIsOpen(true), []);

  const close = useCallback(() => {
    setIsOpen(false);
    setZipFile(null);
    reset();
  }, [reset]);

  const onFileChange = useCallback(
    (name?: string, file?: File | '') => setZipFile(file || null),
    [],
  );

  const start = useCallback(async () => {
    if (!zipFile) return;
    const completed = await processZip(zipFile);
    if (completed) {
      await dispatch(fetchUsers());
    }
  }, [processZip, zipFile, dispatch]);

  const uploaded = results.filter(item => item.status === BULK_AVATAR_STATUS.UPLOADED);
  const failed = results.filter(item => item.status !== BULK_AVATAR_STATUS.UPLOADED);

  const reasonText = useCallback(
    (item: BulkAvatarResult) => {
      const reason = reasonByStatus[item.status] ?? '';
      return item.message ? `${reason} — ${item.message}` : reason;
    },
    [reasonByStatus],
  );

  const downloadFailed = useCallback(() => {
    const csvContent = [
      [lang.BULK_AVATAR_FILE, lang.EMAIL, lang.BULK_AVATAR_REASON].map(csvCell).join(','),
      ...failed.map(item => [item.fileName, item.email, reasonText(item)].map(csvCell).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'failed_profile_pictures.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [failed, lang, reasonText]);

  const resultColumns = [
    { headerName: lang.BULK_AVATAR_FILE, field: 'fileName', flex: 2 },
    { headerName: lang.EMAIL, field: 'email', flex: 2 },
    {
      headerName: lang.BULK_AVATAR_STATUS,
      field: 'status',
      minWidth: 130,
      maxWidth: 130,
      sortable: false,
      filter: {
        component: RadioSetFilter,
        doesFilterPass: radioSetFilterDoesFilterPass,
      },
      filterParams: {
        options: [
          {
            value: STATUS_FILTER.UPLOADED,
            label: lang.BULK_AVATAR_UPLOADED,
            matcher: (value: BulkAvatarStatus) => value === BULK_AVATAR_STATUS.UPLOADED,
          },
          {
            value: STATUS_FILTER.FAILED,
            label: lang.BULK_AVATAR_FAILED,
            matcher: (value: BulkAvatarStatus) => value !== BULK_AVATAR_STATUS.UPLOADED,
          },
        ],
      },
      cellRenderer: (params: { value: BulkAvatarStatus }) =>
        params.value === BULK_AVATAR_STATUS.UPLOADED ? (
          <Tag
            className={styles.statusTag}
            color="success"
            icon={<FontAwesomeIcon icon={faCheckCircle} />}>
            {lang.BULK_AVATAR_UPLOADED}
          </Tag>
        ) : (
          <Tag
            className={styles.statusTag}
            color="error"
            icon={<FontAwesomeIcon icon={faTimesCircle} />}>
            {lang.BULK_AVATAR_FAILED}
          </Tag>
        ),
    },
    {
      headerName: lang.BULK_AVATAR_REASON,
      colId: 'reason',
      flex: 3,
      valueGetter: (params: { data: BulkAvatarResult }) =>
        params.data.status === BULK_AVATAR_STATUS.UPLOADED ? '' : reasonText(params.data),
    },
  ];

  const footerByStage = {
    [BULK_AVATAR_STAGES.UPLOAD]: [
      <Button key="cancel" onClick={close}>
        {generalLang.CANCEL}
      </Button>,
      <Button key="start" type="primary" disabled={!zipFile} onClick={start}>
        {lang.BULK_AVATAR_START}
      </Button>,
    ],
    [BULK_AVATAR_STAGES.PROCESSING]: null,
    [BULK_AVATAR_STAGES.RESULTS]: [
      <Button key="done" type="primary" onClick={close}>
        {lang.BULK_AVATAR_DONE}
      </Button>,
    ],
  };

  return (
    <>
      <Button
        ghost
        type="primary"
        className={styles.trigger}
        data-testid="bulk-avatar-upload-open"
        onClick={open}>
        {lang.BULK_UPLOAD_PHOTOS}
      </Button>
      <Modal
        title={lang.BULK_AVATAR_MODAL_TITLE}
        open={isOpen}
        onCancel={close}
        footer={footerByStage[stage]}
        width={stage === BULK_AVATAR_STAGES.RESULTS ? 800 : 560}
        maskClosable={false}
        loading={stage === BULK_AVATAR_STAGES.PROCESSING}>
        {stage === BULK_AVATAR_STAGES.UPLOAD && (
          <div className={styles.uploadStage}>
            {processError && (
              <Alert
                type="error"
                showIcon
                message={lang.BULK_AVATAR_PROCESS_ERROR}
                description={
                  processError.type === BULK_AVATAR_PROCESS_ERRORS.EMPTY_ZIP
                    ? lang.BULK_AVATAR_NO_IMAGES_FOUND
                    : processError.detail
                }
              />
            )}
            <Text type="secondary" className={styles.help}>
              {lang.BULK_AVATAR_HELP}
            </Text>
            <FileUpload
              id="bulk-avatar-zip"
              name="bulk-avatar-zip"
              accept={ZIP_ACCEPT}
              maxFiles={1}
              allowClear
              onChange={onFileChange}
            />
          </div>
        )}

        {stage === BULK_AVATAR_STAGES.PROCESSING && (
          <div className={styles.processingStage}>
            <Progress
              percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
            />
            <Text type="secondary">
              {lang.BULK_AVATAR_PROGRESS.replace('{done}', formatNumber(progress.done)).replace(
                '{total}',
                formatNumber(progress.total),
              )}
            </Text>
          </div>
        )}

        {stage === BULK_AVATAR_STAGES.RESULTS && (
          <div className={styles.resultsStage}>
            <div className={styles.summary}>
              <div className={styles.stat}>
                <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
                <div>
                  <Text strong className={styles.statCount}>
                    {formatNumber(uploaded.length)}
                  </Text>
                  <br />
                  <Text type="secondary">{lang.BULK_AVATAR_UPLOADED}</Text>
                </div>
              </div>
              <div className={styles.stat}>
                <FontAwesomeIcon icon={faTimesCircle} className={styles.errorIcon} />
                <div>
                  <Text strong className={styles.statCount}>
                    {formatNumber(failed.length)}
                  </Text>
                  <br />
                  <Text type="secondary">{lang.BULK_AVATAR_FAILED}</Text>
                </div>
              </div>
              {failed.length > 0 && (
                <Button
                  className={styles.downloadButton}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={downloadFailed}
                  danger>
                  {lang.BULK_AVATAR_DOWNLOAD_FAILED}
                </Button>
              )}
            </div>
            <Table
              columnDefs={resultColumns}
              rowData={results}
              getRowId={params => String(params.data.id)}
              domLayout="autoHeight"
              pagination={results.length > 10}
              paginationPageSize={10}
            />
          </div>
        )}
      </Modal>
    </>
  );
}
